import apiClient from './api-client';

export type MarkFrequency =
  | 'Daily'
  | 'Weekly'
  | 'Monthly'
  | 'Quarterly'
  | 'Semi-Annual'
  | 'Annual';

export interface LaunchConfig {
  name?: string;
  valuation_date?: string;
  settlement_date?: string;
  updated_at?: string;
  client?: string;
  client_legal_name?: string;
  portfolio_fund?: string;
  sub_portfolio?: string;
  reporting_period?: string;
  mark_frequency: MarkFrequency | null;
}

export interface LaunchConfigUpdate {
  valuation_date?: string;
  settlement_date?: string | null;
  client?: string | null;
  client_legal_name?: string | null;
  portfolio_fund?: string | null;
  sub_portfolio?: string | null;
  reporting_period?: string | null;
  mark_frequency: MarkFrequency | null;
}

export interface RefreshResponse {
  status: string;
  message: string;
  duration_seconds: number;
  details: {
    [key: string]: {
      status: string;
      rows_updated?: number;
      error?: string;
    };
  };
}

export const launchConfigService = {
  /**
   * Get current launch configuration
   */
  async getCurrentConfig(): Promise<LaunchConfig> {
    const response = await apiClient.get('/api/v1/launch-config/current', {
      timeout: 10000, // 10 seconds timeout
    });
    return response.data;
  },

  /**
   * Get current valuation date
   */
  async getValuationDate(): Promise<string> {
    const response = await apiClient.get(
      '/api/v1/launch-config/valuation-date'
    );
    return response.data.valuation_date;
  },

  /**
   * Get current settlement date
   */
  async getSettlementDate(): Promise<string> {
    const response = await apiClient.get(
      '/api/v1/launch-config/settlement-date'
    );
    return response.data.settlement_date;
  },

  /**
   * Update valuation date
   */
  async updateValuationDate(valuation_date: string): Promise<LaunchConfig> {
    const response = await apiClient.put(
      `/api/v1/launch-config/valuation-date?valuation_date=${valuation_date}`
    );
    return response.data.config || response.data;
  },

  /**
   * Update settlement date
   */
  async updateSettlementDate(settlement_date: string): Promise<LaunchConfig> {
    const response = await apiClient.put(
      `/api/v1/launch-config/settlement-date?settlement_date=${settlement_date}`
    );
    return response.data.config || response.data;
  },

  /**
   * Update both dates together
   */
  async updateConfig(data: LaunchConfigUpdate): Promise<LaunchConfig> {
    const response = await apiClient.put('/api/v1/launch-config/', data);
    return response.data.config || response.data;
  },

  /**
   * Refresh materialized views with current config dates
   */
  async refreshMaterializedViews(): Promise<RefreshResponse> {
    // Use a dedicated timeout of 220 seconds for this endpoint
    // instead of waiting for the global 600-second timeout.
    const response = await apiClient.post<RefreshResponse>(
      '/api/v1/valuation/refresh-materialized-views',
      {},
      {
        timeout: 220_000, // 220 seconds
      }
    );
    return response.data;
  },

  /**
   * Refresh materialized views with custom dates
   */
  async refreshWithCustomDates(
    valuation_date: string,
    settlement_date: string,
    update_config = false
  ): Promise<RefreshResponse> {
    const url = `/api/v1/valuation/refresh-with-dates?valuation_date=${valuation_date}&settlement_date=${settlement_date}&update_config=${update_config}`;

    const response = await apiClient.post<RefreshResponse>(url, {});
    return response.data;
  },
};
